import React, { useEffect, useState } from 'react';
import { StickyNote, Plus, Edit2, Trash2, X, Check, Share2, Users, Search, MessageSquare, Send, User, ChevronDown, ChevronRight, Clock } from 'lucide-react';
import { collection, addDoc, query, where, getDocs, doc, updateDoc, arrayUnion, arrayRemove, getDoc, Timestamp, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import { toast } from 'react-hot-toast';
import { useThemeStore } from '../../stores/themeStore';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface Note {
  id: string;
  content: string;
  createdAt: Date;
  color: string;
  author: string;
  userId: string;
  sharedWith?: string[];
  comments?: Comment[];
}

interface Comment {
  id: string;
  content: string;
  createdAt: Date;
  authorId: string;
  authorName: string;
  authorPhotoURL?: string;
}

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  photoURL?: string;
}

export function DashboardNotes() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [showAddNote, setShowAddNote] = useState(false);
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [noteContent, setNoteContent] = useState('');
  const [selectedColor, setSelectedColor] = useState('#FEF3C7'); // Default yellow
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [expandedNoteId, setExpandedNoteId] = useState<string | null>(null);
  const [newComments, setNewComments] = useState<{[key: string]: string}>({});
  const [usersMap, setUsersMap] = useState<Map<string, User>>(new Map());
  const { darkMode } = useThemeStore();

  const colors = [
    { value: '#FEF3C7', label: 'Jaune' },
    { value: '#DBEAFE', label: 'Bleu' },
    { value: '#DCF9E6', label: 'Vert' },
    { value: '#FEE2E2', label: 'Rouge' },
    { value: '#F3E8FF', label: 'Violet' }
  ];

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = async () => {
    if (!auth.currentUser) return;

    try {
      const q = query(
        collection(db, 'dashboard_notes'),
        where('userId', '==', auth.currentUser.uid)
      );
      
      const querySnapshot = await getDocs(q);
      const loadedNotes: Note[] = [];
      
      // Récupérer les informations des utilisateurs pour les commentaires
      const userIds = new Set<string>();
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        
        // Ajouter les IDs des utilisateurs avec qui la note est partagée
        if (data.sharedWith && Array.isArray(data.sharedWith)) {
          data.sharedWith.forEach(userId => userIds.add(userId));
        }
        
        // Ajouter les IDs des auteurs de commentaires
        if (data.comments && Array.isArray(data.comments)) {
          data.comments.forEach(comment => {
            if (comment.authorId) {
              userIds.add(comment.authorId);
            }
          });
        }
      });
      
      // Charger les informations des utilisateurs
      const usersMap = new Map<string, User>();
      for (const userId of userIds) {
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            usersMap.set(userId, { 
              id: userId,
              email: userData.email,
              firstName: userData.firstName,
              lastName: userData.lastName,
              role: userData.role,
              photoURL: userData.photoURL
            });
          }
        } catch (error) {
          console.error(`Error loading user ${userId}:`, error);
        }
      }
      
      setUsersMap(usersMap);
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        loadedNotes.push({
          id: doc.id,
          content: data.content,
          createdAt: data.createdAt.toDate(),
          color: data.color,
          author: data.author,
          userId: data.userId,
          sharedWith: data.sharedWith || [],
          comments: data.comments ? data.comments.map(c => ({
            ...c,
            createdAt: c.createdAt instanceof Timestamp ? c.createdAt.toDate() : new Date(c.createdAt)
          })) : []
        });
      });
      
      // Trier les notes par date (plus récentes en premier)
      loadedNotes.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      
      setNotes(loadedNotes);
    } catch (error) {
      console.error('Error loading notes:', error);
      toast.error('Erreur lors du chargement des notes');
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim() || !auth.currentUser) return;

    try {
      const newNote: Note = {
        id: crypto.randomUUID(),
        content: noteContent.trim(),
        createdAt: new Date(),
        color: selectedColor,
        author: auth.currentUser.displayName || "Utilisateur",
        userId: auth.currentUser.uid,
        sharedWith: [],
        comments: []
      };

      // Ajouter la note à Firestore
      const docRef = await addDoc(collection(db, 'dashboard_notes'), {
        content: newNote.content,
        createdAt: newNote.createdAt,
        color: newNote.color,
        author: newNote.author,
        userId: auth.currentUser.uid,
        sharedWith: [],
        comments: []
      });

      // Mettre à jour l'ID avec celui généré par Firestore
      newNote.id = docRef.id;
      
      setNotes(prev => [newNote, ...prev]);
      setNoteContent('');
      setShowAddNote(false);
      toast.success('Note ajoutée');
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Erreur lors de l\'ajout de la note');
    }
  };

  const handleEditNote = async (noteId: string) => {
    if (!noteContent.trim() || !auth.currentUser) return;

    try {
      const updatedNote = {
        content: noteContent.trim(),
        updatedAt: new Date()
      };

      // Mettre à jour la note dans Firestore
      await updateDoc(doc(db, 'dashboard_notes', noteId), updatedNote);

      // Mettre à jour l'état local
      setNotes(prev => prev.map(note => 
        note.id === noteId 
          ? { ...note, content: noteContent.trim() }
          : note
      ));
      
      setEditingNote(null);
      setNoteContent('');
      toast.success('Note mise à jour');
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error('Erreur lors de la mise à jour de la note');
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette note ?')) return;

    try {
      // Supprimer la note de Firestore
      await deleteDoc(doc(db, 'dashboard_notes', noteId));

      // Mettre à jour l'état local
      setNotes(prev => prev.filter(note => note.id !== noteId));
      toast.success('Note supprimée');
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Erreur lors de la suppression de la note');
    }
  };

  const handleShareNote = (note: Note) => {
    setSelectedNote(note);
    setShowShareModal(true);
    loadUsers();
  };

  const loadUsers = async () => {
    if (!auth.currentUser) return;
    
    setIsLoading(true);
    try {
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);
      
      const usersList: User[] = [];
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Ne pas inclure l'utilisateur actuel dans la liste
        if (doc.id !== auth.currentUser?.uid) {
          usersList.push({
            id: doc.id,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            role: data.role,
            photoURL: data.photoURL
          });
        }
      });
      
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShareWithUser = async (user: User) => {
    if (!selectedNote || !auth.currentUser) return;
    
    try {
      // Mettre à jour le document de la note pour ajouter l'utilisateur à la liste de partage
      const noteRef = doc(db, 'dashboard_notes', selectedNote.id);
      
      // Vérifier si la note existe
      const noteDoc = await getDoc(noteRef);
      if (!noteDoc.exists()) {
        toast.error('Note introuvable');
        return;
      }
      
      // Mettre à jour Firestore
      await updateDoc(noteRef, {
        sharedWith: arrayUnion(user.id)
      });
      
      // Mettre à jour l'état local
      setNotes(prev => prev.map(note => {
        if (note.id === selectedNote.id) {
          const updatedSharedWith = [...(note.sharedWith || [])];
          if (!updatedSharedWith.includes(user.id)) {
            updatedSharedWith.push(user.id);
          }
          return { ...note, sharedWith: updatedSharedWith };
        }
        return note;
      }));
      
      // Mettre à jour la note sélectionnée
      setSelectedNote(prev => {
        if (!prev) return null;
        const updatedSharedWith = [...(prev.sharedWith || [])];
        if (!updatedSharedWith.includes(user.id)) {
          updatedSharedWith.push(user.id);
        }
        return { ...prev, sharedWith: updatedSharedWith };
      });
      
      toast.success(`Note partagée avec ${user.email}`);
    } catch (error) {
      console.error('Error sharing note:', error);
      toast.error('Erreur lors du partage de la note');
    }
  };

  const handleRemoveShare = async (user: User) => {
    if (!selectedNote || !auth.currentUser) return;
    
    try {
      // Mettre à jour le document de la note pour retirer l'utilisateur de la liste de partage
      const noteRef = doc(db, 'dashboard_notes', selectedNote.id);
      
      // Vérifier si la note existe
      const noteDoc = await getDoc(noteRef);
      if (!noteDoc.exists()) {
        toast.error('Note introuvable');
        return;
      }
      
      // Mettre à jour Firestore
      await updateDoc(noteRef, {
        sharedWith: arrayRemove(user.id)
      });
      
      // Mettre à jour l'état local
      setNotes(prev => prev.map(note => {
        if (note.id === selectedNote.id) {
          return { 
            ...note, 
            sharedWith: (note.sharedWith || []).filter(id => id !== user.id) 
          };
        }
        return note;
      }));
      
      // Mettre à jour la note sélectionnée
      setSelectedNote(prev => {
        if (!prev) return null;
        return { 
          ...prev, 
          sharedWith: (prev.sharedWith || []).filter(id => id !== user.id) 
        };
      });
      
      toast.success(`Partage retiré pour ${user.email}`);
    } catch (error) {
      console.error('Error removing share:', error);
      toast.error('Erreur lors du retrait du partage');
    }
  };

  const handleAddComment = async (noteId: string) => {
    if (!newComments[noteId]?.trim() || !auth.currentUser) return;
    
    try {
      const userInfo = getUserInfo(auth.currentUser.uid);
      
      const comment: Comment = {
        id: crypto.randomUUID(),
        content: newComments[noteId].trim(),
        createdAt: new Date(),
        authorId: auth.currentUser.uid,
        authorName: userInfo.name
      };
      
      // Mettre à jour la note dans Firestore
      const noteRef = doc(db, 'dashboard_notes', noteId);
      await updateDoc(noteRef, {
        comments: arrayUnion({
          ...comment,
          createdAt: Timestamp.fromDate(comment.createdAt)
        })
      });
      
      // Mettre à jour l'état local
      setNotes(prev => prev.map(note => {
        if (note.id === noteId) {
          return {
            ...note,
            comments: [...(note.comments || []), comment]
          };
        }
        return note;
      }));
      
      // Réinitialiser le champ de commentaire
      setNewComments(prev => ({
        ...prev,
        [noteId]: ''
      }));
      
      toast.success('Commentaire ajouté');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Erreur lors de l\'ajout du commentaire');
    }
  };

  const toggleNoteExpansion = (noteId: string) => {
    setExpandedNoteId(expandedNoteId === noteId ? null : noteId);
  };

  const getUserDisplayName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email.split('@')[0];
  };

  const getUserInitials = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user.email[0].toUpperCase();
  };

  const getUserInfo = (userId: string) => {
    if (userId === auth.currentUser?.uid) {
      const displayName = auth.currentUser.displayName || 'Utilisateur';
      const nameParts = displayName.split(' ');
      let initials = '';
      
      if (nameParts.length >= 2) {
        initials = `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      } else if (nameParts.length === 1 && nameParts[0].length > 0) {
        initials = nameParts[0][0].toUpperCase();
      } else {
        initials = 'U';
      }
      
      return {
        name: displayName,
        initials: initials,
        photoURL: auth.currentUser.photoURL
      };
    }
    
    const user = usersMap.get(userId);
    if (!user) return { name: 'Utilisateur', initials: 'U', photoURL: null };
    
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 
                 user.email.split('@')[0];
    
    let initials = '';
    if (user.firstName && user.lastName) {
      initials = `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    } else if (name.length > 0) {
      const nameParts = name.split(' ');
      if (nameParts.length >= 2) {
        initials = `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      } else {
        initials = name[0].toUpperCase();
      }
    } else {
      initials = 'U';
    }
    
    return {
      name,
      initials,
      photoURL: user.photoURL
    };
  };

  const formatDate = (date: Date) => {
    return format(date, 'dd/MM/yyyy', { locale: fr });
  };

  const formatTime = (date: Date) => {
    return format(date, 'HH:mm', { locale: fr });
  };

  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      (user.firstName && user.firstName.toLowerCase().includes(searchLower)) ||
      (user.lastName && user.lastName.toLowerCase().includes(searchLower)) ||
      (user.role && user.role.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm`}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className={`text-lg font-semibold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <StickyNote className="w-5 h-5 mr-2 text-blue-600" />
            Notes
          </h2>
          <button
            onClick={() => setShowAddNote(true)}
            className={`flex items-center px-3 py-1.5 text-sm ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-50 text-blue-600'} rounded-lg hover:${darkMode ? 'bg-blue-800' : 'bg-blue-100'}`}
          >
            <Plus className="w-4 h-4 mr-1" />
            Nouvelle note
          </button>
        </div>

        {/* Formulaire d'ajout/édition */}
        {(showAddNote || editingNote) && (
          <div className={`mb-6 p-4 border ${darkMode ? 'border-gray-700' : 'border-gray-200'} rounded-lg`}>
            <textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Contenu de la note..."
              className={`w-full p-2 border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} rounded-lg mb-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              rows={3}
            />
            
            {showAddNote && (
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Couleur :</span>
                {colors.map(color => (
                  <button
                    key={color.value}
                    onClick={() => setSelectedColor(color.value)}
                    className={`w-6 h-6 rounded-full ${
                      selectedColor === color.value ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                  />
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowAddNote(false);
                  setEditingNote(null);
                  setNoteContent('');
                }}
                className={`flex items-center px-3 py-1.5 text-sm ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'} rounded-lg`}
              >
                <X className="w-4 h-4 mr-1" />
                Annuler
              </button>
              <button
                onClick={() => editingNote ? handleEditNote(editingNote) : handleAddNote()}
                className={`flex items-center px-3 py-1.5 text-sm ${darkMode ? 'bg-blue-700 text-white' : 'bg-blue-600 text-white'} rounded-lg hover:${darkMode ? 'bg-blue-600' : 'bg-blue-700'}`}
              >
                <Check className="w-4 h-4 mr-1" />
                {editingNote ? 'Enregistrer' : 'Ajouter'}
              </button>
            </div>
          </div>
        )}

        {/* Liste des notes */}
        <div className="space-y-4">
          {notes.map((note) => {
            const currentUserInfo = auth.currentUser ? getUserInfo(auth.currentUser.uid) : { name: 'Utilisateur', initials: 'U' };
            
            return (
              <div
                key={note.id}
                className="rounded-lg overflow-hidden"
              >
                <div
                  className="p-4 rounded-lg relative group"
                  style={{ backgroundColor: note.color }}
                  onClick={() => toggleNoteExpansion(note.id)}
                >
                  <div className="absolute top-2 right-2 flex gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShareNote(note);
                      }}
                      className="p-1 hover:bg-white/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Partager cette note"
                    >
                      <Share2 className="w-4 h-4 text-gray-800" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingNote(note.id);
                        setNoteContent(note.content);
                      }}
                      className="p-1 hover:bg-white/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Edit2 className="w-4 h-4 text-gray-800" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNote(note.id);
                      }}
                      className="p-1 hover:bg-white/20 rounded-lg"
                      title="Supprimer cette note"
                    >
                      <Trash2 className="w-4 h-4 text-gray-800" />
                    </button>
                  </div>
                  <p className="whitespace-pre-wrap pr-20 text-gray-800">{note.content}</p>
                  <div className="mt-2 flex items-center justify-between text-sm text-gray-700">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      <span>{formatDate(note.createdAt)}</span>
                      <div className="ml-2 w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-medium">
                        {currentUserInfo.initials}
                      </div>
                    </div>
                    <div className="flex items-center">
                      {note.sharedWith && note.sharedWith.length > 0 && (
                        <div className="flex items-center mr-2">
                          <Users className="w-4 h-4 mr-1 text-blue-500" />
                          <span className="text-blue-500">{note.sharedWith.length}</span>
                        </div>
                      )}
                      {note.comments && note.comments.length > 0 && (
                        <div className="flex items-center">
                          <MessageSquare className="w-4 h-4 mr-1 text-gray-700" />
                          <span className="text-gray-700">{note.comments.length}</span>
                        </div>
                      )}
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleNoteExpansion(note.id);
                        }}
                        className="ml-2 p-1 hover:bg-black/5 rounded-full"
                      >
                        {expandedNoteId === note.id ? 
                          <ChevronDown className="w-4 h-4 text-gray-700" /> : 
                          <ChevronRight className="w-4 h-4 text-gray-700" />
                        }
                      </button>
                    </div>
                  </div>
                </div>
                
                {expandedNoteId === note.id && (
                  <div className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} p-4 rounded-b-lg mt-1`}>
                    <div className="mb-4">
                      <h4 className={`text-sm font-medium mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <MessageSquare className="w-4 h-4 mr-1" />
                        Commentaires ({note.comments?.length || 0})
                      </h4>
                      
                      <div className={`space-y-3 max-h-60 overflow-y-auto ${note.comments?.length ? 'mb-4' : ''}`}>
                        {note.comments?.map((comment) => {
                          const userInfo = getUserInfo(comment.authorId);
                          const isOwnComment = comment.authorId === auth.currentUser?.uid;
                          
                          return (
                            <div 
                              key={comment.id} 
                              className={`flex ${isOwnComment ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`flex max-w-[80%] ${isOwnComment ? 'flex-row-reverse' : 'flex-row'}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                  isOwnComment 
                                    ? (darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700')
                                    : (darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700')
                                }`}>
                                  {userInfo.initials}
                                </div>
                                
                                <div className={`mx-2 px-3 py-2 rounded-lg ${
                                  isOwnComment 
                                    ? (darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700')
                                    : (darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700')
                                }`}>
                                  <p className="text-sm">{comment.content}</p>
                                  <div className={`text-xs mt-1 ${
                                    darkMode 
                                      ? (isOwnComment ? 'text-blue-400' : 'text-gray-400')
                                      : (isOwnComment ? 'text-blue-500' : 'text-gray-500')
                                  }`}>
                                    <span>{userInfo.name}</span>
                                    <span className="mx-1">•</span>
                                    <span>{formatDate(comment.createdAt)}</span>
                                    <span className="mx-1">•</span>
                                    <span>{formatTime(comment.createdAt)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        
                        {!note.comments?.length && (
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Aucun commentaire pour le moment
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="text"
                          placeholder="Ajouter un commentaire..."
                          value={newComments[note.id] || ''}
                          onChange={(e) => setNewComments({
                            ...newComments,
                            [note.id]: e.target.value
                          })}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && newComments[note.id]?.trim()) {
                              handleAddComment(note.id);
                            }
                          }}
                          className={`flex-1 rounded-l-lg border ${
                            darkMode 
                              ? 'border-gray-600 bg-gray-600 text-white' 
                              : 'border-gray-300 bg-white text-gray-900'
                          } p-2 text-sm`}
                        />
                        <button
                          onClick={() => handleAddComment(note.id)}
                          disabled={!newComments[note.id]?.trim()}
                          className={`p-2 rounded-r-lg ${
                            darkMode 
                              ? 'bg-blue-700 text-white hover:bg-blue-600' 
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          } disabled:opacity-50`}
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {notes.length === 0 && !showAddNote && (
            <p className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'} py-4`}>
              Aucune note pour le moment
            </p>
          )}
        </div>
      </div>

      {/* Modal de partage */}
      {showShareModal && selectedNote && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto`}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-xl font-semibold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  <Share2 className="w-5 h-5 mr-2 text-blue-600" />
                  Partager la note
                </h2>
                <button onClick={() => setShowShareModal(false)} className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}>
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-4">
                <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: selectedNote.color }}>
                  <p className="whitespace-pre-wrap text-gray-800">{selectedNote.content}</p>
                </div>
              </div>

              <div className="mb-4">
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Partagée avec ({selectedNote.sharedWith?.length || 0})
                </label>
                
                {selectedNote.sharedWith && selectedNote.sharedWith.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedNote.sharedWith.map(userId => {
                      const user = users.find(u => u.id === userId);
                      if (!user) return null;
                      
                      return (
                        <div 
                          key={user.id} 
                          className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                        >
                          <div className="flex items-center gap-3">
                            {user.photoURL ? (
                              <img 
                                src={user.photoURL} 
                                alt={getUserDisplayName(user)} 
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                                {getUserInitials(user)}
                              </div>
                            )}
                            <div>
                              <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {getUserDisplayName(user)}
                              </div>
                              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {user.email}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveShare(user)}
                            className={`px-3 py-1 text-xs rounded-lg ${darkMode ? 'bg-red-900 text-red-300 hover:bg-red-800' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                          >
                            Retirer
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Cette note n'est partagée avec personne pour le moment.
                  </p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Partager avec d'autres utilisateurs
                </label>
                <input
                  type="text"
                  placeholder="Rechercher un utilisateur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5 mb-4`}
                />

                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {isLoading ? (
                    <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Chargement des utilisateurs...
                    </div>
                  ) : filteredUsers.length > 0 ? (
                    filteredUsers
                      .filter(user => !selectedNote.sharedWith?.includes(user.id))
                      .map(user => (
                        <div 
                          key={user.id} 
                          className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`}
                        >
                          <div className="flex items-center gap-3">
                            {user.photoURL ? (
                              <img 
                                src={user.photoURL} 
                                alt={getUserDisplayName(user)} 
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                                {getUserInitials(user)}
                              </div>
                            )}
                            <div>
                              <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {getUserDisplayName(user)}
                              </div>
                              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {user.email}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleShareWithUser(user)}
                            className={`flex items-center px-3 py-1 text-xs rounded-lg ${darkMode ? 'bg-blue-900 text-blue-300 hover:bg-blue-800' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                          >
                            <Share2 className="w-3 h-3 mr-1" />
                            Partager
                          </button>
                        </div>
                      ))
                  ) : (
                    <p className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {searchTerm ? 'Aucun utilisateur trouvé' : 'Commencez à taper pour rechercher des utilisateurs'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}